/**
 * Download Info Requests aka Download leads
 * -----------------------------------------
 * This type of lead is generated after downloading a file.
 * If the file is a product file, this will generate a DOWNLOAD_INFO_REQUEST (inheriting from the InfoRequest model, aka Single Quote)
 * If the file is a company file, this will generate an ACCOUNT_DOWNLOAD_INFO_REQUEST (inheriting from the AccountInfoRequest model, aka Contact)
 *
 * Some restrictions for the downloads and lead generation:
 * - Only registered users can download files
 * - If the user is not within the company business countries, the file will be downloaded, but the download won't generate any leads
 * - After a lead was generated, no other leads will be generated for the same company or product within 24 hours, i.e. only the first download will generate a lead, others will only download the file.
 * - After a download, the product is added to a special folder in MyAD, even if there wasn't a lead.
 */
import { renderRecaptcha, executeRecaptcha, resetRecaptcha } from '../../helpers/validator';
import { FORMS, PAGES_WITH_DOWNLOAD_IR, ALLOW_PRODUCT_DOWNLOAD_IR } from '../../helpers/constants';
import { getDevice, requireAuthToDownload, requireCountryCode, trackRegisteredDownload } from '../../helpers/util';
import { persistValueToCookies } from '../../helpers/cookies'
import { buildFormData } from '../../helpers/form-helpers';
import { itemHasLeads, getRecentlyQuotedProducts, updateQuotedProducts, showTooltipAfterDownload, updateAccountLeads, getRecentAccountLeads } from './helpers';

let currentLeadType;
let userIsInBusinessCountries = false;
let currentProduct, currentAccount, currentFile, currentLink, downloadInfoRequestURL;
let quotedProducts = [];
let accountLeads = [];

const PRODUCT_LEAD_TYPE = 'product-lead'
const ACCOUNT_LEAD_TYPE = 'account-lead'

const submitImplicitQuoteAfterDownload = link => {
  const { fileId, accountId, productId, allowQuote, href: linkUrl, source, leadUrl } = link.dataset;
  if (!fileId) return;
  currentLeadType = productId ? PRODUCT_LEAD_TYPE : ACCOUNT_LEAD_TYPE
  trackRegisteredDownload(link);

  currentFile = fileId;
  currentProduct = productId;
  currentAccount = accountId;
  currentLink = linkUrl;
  downloadInfoRequestURL = leadUrl // URL is set in template depending on the type of file

  if (!userIsInBusinessCountries || !isQuotable(currentLeadType, currentProduct || currentAccount) || (currentProduct && !allowQuote)) {
    console.info(`File Download without lead - ${currentLeadType}`) // eslint-disable-line no-console
    window.open(linkUrl, currentFile) // download directly if not quotable
    currentProduct = currentAccount = currentFile = currentLink = null
    return true
  }

  const currentFormType = source === 'widget' ? FORMS.WIDGET_QUOTE_AFTER_DOWNLOAD : FORMS.QUOTE_AFTER_DOWNLOAD
  executeRecaptcha(currentFormType)
  return true
}

const submitForm = async recaptchaToken => {
  console.info(`File Download with lead - ${currentLeadType}`) // eslint-disable-line no-console
  window.open(currentLink, 'download') // download after recaptcha challenge has been passed if quotable

  const rawFormData = {
    'source': 'nft',
    'info_request[ad_source]': 'neufert',
    'info_request[referrer]': window.location.href,
    'info_request[device]': getDevice(),
    'info_request[kenneth_id]': window.AD.CurrentUser.id,
    'g-recaptcha-response': recaptchaToken
  }

  if (currentProduct) {
    rawFormData['info_request[product_file_id]'] = currentFile;
    rawFormData['info_request[product_id]'] = currentProduct;
  }

  if (currentAccount) {
    rawFormData['info_request[account_id]'] = currentAccount;
    rawFormData['info_request[account_file_id]'] = currentFile;
  }

  const formData = buildFormData(rawFormData)

  const response = await fetch(downloadInfoRequestURL, { method: 'POST', body: formData });

  if (!response.ok) {
    console.error('Error processing download info request'); // eslint-disable-line no-console
  } else {
    const { data: { geolocation } } = await response.json();
    if (geolocation) {
      Object.keys(geolocation).map(key => geolocation[key] ? persistValueToCookies(key, geolocation[key], 'nft_info_request') : null)
    }
    if (currentProduct) {
      quotedProducts = updateQuotedProducts(quotedProducts, currentProduct)
    } else {
      accountLeads = updateAccountLeads(accountLeads, currentAccount)
    }
    window.ga('send', 'event', 'download-info-request', 'submit', 'sent'); // maybe not needed anymore
  }

  currentProduct = currentAccount = currentFile = currentLink = null
  resetRecaptcha(FORMS.QUOTE_AFTER_DOWNLOAD)
}

const checkIfuserIsInBusinessCountries = () => {
  const businessCountriesData = document.querySelector('.js-business-countries-data');
  const businessCountries = businessCountriesData ? JSON.parse(businessCountriesData.dataset['businessCountries']) : [];
  return businessCountries.includes(window.AFD.countryCode)
}

const isQuotable = (type, leadItemId) => {
  if (type === PRODUCT_LEAD_TYPE) {
    return !!quotedProducts && !itemHasLeads(quotedProducts, leadItemId);
  }

  return !!accountLeads && !itemHasLeads(accountLeads, currentAccount)
}

const openFilesAccordion = e => {
  e.preventDefault()
  const targetFilesAccordion = document.querySelector('#accordion-supplementary .acordeon__category__title, #accordion-catalog .acordeon__category__title')
  if (!targetFilesAccordion) return
  const offsetTop = targetFilesAccordion.getBoundingClientRect().top
  window.scroll({ behavior: 'smooth', top: offsetTop - 57 })
  targetFilesAccordion.click()
}

/**
 * Initialize all logic and validations for file download and quoting
 * @param  {boolean} pageHasProduct - flag to check whether to prepare a download info request or account download info request
 */
const initialize = pageHasProducts => {
  userIsInBusinessCountries = checkIfuserIsInBusinessCountries()

  if (pageHasProducts) {
    quotedProducts = getRecentlyQuotedProducts();
  }
  accountLeads = getRecentAccountLeads();

  const fileItems = document.querySelectorAll('.js-download-file-item');
  fileItems.forEach(item => item.addEventListener('click', e => {
    if (e.target.matches('#js-downloads-folder-link')) {
      e.stopPropagation()
      return true
    }

    const [ link ] = item.children;

    requireAuthToDownload(e, link, submitImplicitQuoteAfterDownload) &&
    pageHasProducts && showTooltipAfterDownload(link);
  }, true));

  const seeAllFilesBtn = document.getElementById('js-see-all-files')
  seeAllFilesBtn && seeAllFilesBtn.addEventListener('click', openFilesAccordion)

  renderRecaptcha(document.getElementById('js-recaptcha-placeholder'), submitForm, FORMS.QUOTE_AFTER_DOWNLOAD)

  const widgetRecaptcha = document.getElementById('js-widget-recaptcha-placeholder')
  widgetRecaptcha && renderRecaptcha(widgetRecaptcha, submitForm, FORMS.WIDGET_QUOTE_AFTER_DOWNLOAD)
}

export function downloadIrSetup () {
  const classes = document.body.classList;

  if (!PAGES_WITH_DOWNLOAD_IR.some(page => classes.contains(page))) return;
  const pageHasProducts = ALLOW_PRODUCT_DOWNLOAD_IR.some(page => classes.contains(page));

  requireCountryCode(() => initialize(pageHasProducts));
}
